import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { format, subDays } from 'date-fns';
import SearchBar from './SearchBar';
import TagFilter from './TagFilter';
import PaperCollection from './PaperCollection';
import { paperApi } from '../utils/apiUtils';
import { filterPapers } from '../utils/paperUtils';
import { pageTransition, renderLoadingState, renderErrorState } from '../utils/uiUtils';

function Archive() {
  const [papers, setPapers] = useState([]);
  const [filteredPapers, setFilteredPapers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTag, setSelectedTag] = useState(null);
  const [startDate, setStartDate] = useState(() => format(subDays(new Date(), 7), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(() => format(new Date(), 'yyyy-MM-dd'));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadPapers = async () => {
      try {
        const { papers } = await paperApi.fetchPapersByDateRange(startDate, endDate);
        setPapers(papers);
        setFilteredPapers(papers);
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };
    loadPapers();
  }, [startDate, endDate]);

  const handleSearch = (term) => {
    setSearchTerm(term);
    setFilteredPapers(filterPapers(papers, term, selectedTag));
  };

  const handleTagSelect = (tag) => {
    setSelectedTag(tag);
    setFilteredPapers(filterPapers(papers, searchTerm, tag));
  };

  const handleDateChange = (e) => {
    const { name, value } = e.target;
    if (name === 'startDate') setStartDate(value);
    else setEndDate(value);
  };

  if (loading) return renderLoadingState();
  if (error) return renderErrorState(error);

  return (
    <motion.div {...pageTransition} className="max-w-7xl mx-auto">
      {/* Page header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <span className="text-yellow-300/60 text-xs">{'>'}</span>
          <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-tight">Archive</h1>
          {papers.length > 0 && (
            <span className="text-xs text-white/30 font-semibold tabular-nums">
              [{filteredPapers.length}]
            </span>
          )}
        </div>
        <div className="h-px bg-white/10 mt-3" />
      </div>

      <div className="space-y-4 mb-8">
        <SearchBar onSearch={handleSearch} />
        <TagFilter onTagSelect={handleTagSelect} />

        {/* Date range */}
        <div className="flex flex-wrap items-center gap-3 glass-card rounded-lg p-3">
          <span className="text-[10px] text-white/30 uppercase tracking-widest font-semibold">Range</span>
          <input
            type="date"
            name="startDate"
            value={startDate}
            onChange={handleDateChange}
            className="px-3 py-1.5 rounded-md text-xs bg-white/90 border-0 text-gray-700 focus:outline-none focus:ring-1 focus:ring-yellow-300/50 font-code"
          />
          <span className="text-white/20 text-xs">&rarr;</span>
          <input
            type="date"
            name="endDate"
            value={endDate}
            onChange={handleDateChange}
            className="px-3 py-1.5 rounded-md text-xs bg-white/90 border-0 text-gray-700 focus:outline-none focus:ring-1 focus:ring-yellow-300/50 font-code"
          />
        </div>
      </div>
      <PaperCollection papers={filteredPapers} />
    </motion.div>
  );
}

export default Archive;
